import {create} from 'zustand'
import {AuthState, AuthUser} from '@/features/auth/types.ts'
import {
    IMPERSONATION_ORIGINAL_ACCESS_TOKEN,
    IMPERSONATION_ORIGINAL_REFRESH_TOKEN,
    IMPERSONATION_STARTED_AT,
    LOCAL_STORAGE_ACCESS_TOKEN,
    LOCAL_STORAGE_REFRESH_TOKEN
} from '@/features/auth/constants.ts'
import {updateSocketAuth} from '@/socket'

type AuthStoreActions = {
    reset: () => void
    setAuthUser: (data: AuthUser) => void
    setAuthData: (data: {user: AuthUser; accessToken: string; refreshToken: string}) => void
    startImpersonation: (data: {accessToken: string; user: AuthUser}) => void
    stopImpersonation: () => void
    restoreOriginalSession: () => boolean
}

/**
 * The set-aside original access token is the single source of truth for "an impersonation session is
 * active": deriving the flag from it means the two can never disagree after a partial failure.
 */
const readStoredStartedAt = () => {
    const storedStartedAt = Number(localStorage.getItem(IMPERSONATION_STARTED_AT))
    return Number.isFinite(storedStartedAt) && storedStartedAt > 0 ? storedStartedAt : null
}

/**
 * Copies the operator's set-aside tokens back over the active ones and clears the impersonation keys.
 * Returns false when there was nothing set aside, so callers don't act on a no-op restore.
 * `user` is dropped: callers reload, and App.tsx's userMe() bootstrap repopulates the operator's own profile.
 */
const restoreOriginalSessionInStorage = () => {
    const originalAccessToken = localStorage.getItem(IMPERSONATION_ORIGINAL_ACCESS_TOKEN)
    const originalRefreshToken = localStorage.getItem(IMPERSONATION_ORIGINAL_REFRESH_TOKEN)
    if (!originalAccessToken) return null

    localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, originalAccessToken)
    if (originalRefreshToken) {
        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, originalRefreshToken)
    } else {
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
    }
    localStorage.removeItem(IMPERSONATION_ORIGINAL_ACCESS_TOKEN)
    localStorage.removeItem(IMPERSONATION_ORIGINAL_REFRESH_TOKEN)
    localStorage.removeItem(IMPERSONATION_STARTED_AT)

    return {accessToken: originalAccessToken, refreshToken: originalRefreshToken ?? undefined}
}

export const useAuthStore = create<Partial<AuthState> & AuthStoreActions>()((set, get) => ({
    user: null,
    accessToken: localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN) || undefined,
    refreshToken: localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN) || undefined,
    impersonating: !!localStorage.getItem(IMPERSONATION_ORIGINAL_ACCESS_TOKEN),
    impersonationStartedAt: readStoredStartedAt(),
    reset: () => {
        localStorage.removeItem(LOCAL_STORAGE_ACCESS_TOKEN)
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
        set({
            user: null,
            accessToken: undefined,
            refreshToken: undefined,
            impersonating: false,
            impersonationStartedAt: null
        })
        updateSocketAuth()
    },
    setAuthUser: user => set({user}),
    setAuthData: data => {
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, data.accessToken)
        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, data.refreshToken)
        set({user: data.user, accessToken: data.accessToken, refreshToken: data.refreshToken})
        updateSocketAuth(data.accessToken)
    },
    startImpersonation: ({accessToken, user}) => {
        const originalAccessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)
        const originalRefreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN)
        if (originalAccessToken) localStorage.setItem(IMPERSONATION_ORIGINAL_ACCESS_TOKEN, originalAccessToken)
        if (originalRefreshToken) localStorage.setItem(IMPERSONATION_ORIGINAL_REFRESH_TOKEN, originalRefreshToken)

        // The impersonated session is not renewable, so no refresh token remains active while impersonating.
        const startedAt = Date.now()
        localStorage.setItem(IMPERSONATION_STARTED_AT, String(startedAt))
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, accessToken)
        localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)

        set({
            user,
            accessToken,
            refreshToken: undefined,
            impersonating: true,
            impersonationStartedAt: startedAt
        })
        updateSocketAuth(accessToken)
    },
    restoreOriginalSession: () => {
        const restored = restoreOriginalSessionInStorage()
        if (!restored) return false

        set({
            user: null,
            accessToken: restored.accessToken,
            refreshToken: restored.refreshToken,
            impersonating: false,
            impersonationStartedAt: null
        })
        updateSocketAuth(restored.accessToken)
        return true
    },
    /** Operator-initiated end of impersonation — same operation as any other restore. */
    stopImpersonation: () => void get().restoreOriginalSession()
}))

export const usePermissions = () => useAuthStore(state => state.user?.permissions)
